import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { supabase } from "../lib/supabase";
import type {
  ManagedFileRecord,
  ProjectFileManagerSnapshot,
  ProjectFolder,
} from "../types";
import { useWorkspace } from "./WorkspaceProvider";

function messageFor(reason: unknown, fallback: string) {
  if (reason instanceof Error) return reason.message;
  if (
    reason &&
    typeof reason === "object" &&
    "message" in reason &&
    typeof reason.message === "string"
  )
    return reason.message;
  return fallback;
}

function localSnapshot(
  data: NonNullable<ReturnType<typeof useWorkspace>["data"]>,
  folders: ProjectFolder[] = [],
): ProjectFileManagerSnapshot {
  return {
    project_id: data.project.id,
    viewer_id: data.viewer.id,
    viewer_role: data.viewer.role,
    folders,
    files: data.files.map(
      (file): ManagedFileRecord => ({
        ...file,
        organization_id: data.organization.id,
        folder_id: file.folder_id ?? null,
        updated_at: file.updated_at ?? file.created_at,
        uploaded_by: file.uploaded_by ?? data.viewer.id,
        uploader_name: file.uploader_name ?? data.viewer.full_name,
        archived_at: file.archived_at ?? null,
        archived_by: null,
        effective_archived: file.effective_archived ?? Boolean(file.archived_at),
        upload_status: file.upload_status ?? "READY",
        location_path: file.location_path ?? [],
      }),
    ),
  };
}

export function useProjectFileManager() {
  const { data, refresh, uploadFile, downloadFile } = useWorkspace();
  const { localDemo } = useAuth();
  const [snapshot, setSnapshot] = useState<ProjectFileManagerSnapshot | null>(
    data ? localSnapshot(data) : null,
  );
  const [loading, setLoading] = useState(!localDemo);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!data) return;
    if (localDemo) {
      setSnapshot((current) =>
        localSnapshot(data, current?.folders ?? []),
      );
      setLoading(false);
      return;
    }
    if (!supabase) {
      setError("Supabase no está configurado.");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await supabase.rpc("get_project_file_manager", {
        p_project_id: data.project.id,
      });
      if (result.error) throw result.error;
      if (!result.data || typeof result.data !== "object")
        throw new Error("No se pudo abrir el gestor de archivos.");
      setSnapshot(result.data as ProjectFileManagerSnapshot);
      setError(null);
    } catch (reason) {
      setError(messageFor(reason, "No se pudo abrir el gestor de archivos."));
    } finally {
      setLoading(false);
    }
  }, [data, localDemo]);

  useEffect(() => {
    void load();
  }, [load]);

  const finishRemoteMutation = useCallback(async () => {
    await Promise.all([load(), refresh()]);
  }, [load, refresh]);

  const remoteRpc = useCallback(
    async (name: string, parameters: Record<string, unknown>) => {
      if (!supabase) throw new Error("Supabase no está configurado.");
      const result = await supabase.rpc(name, parameters);
      if (result.error) throw result.error;
      return result.data;
    },
    [],
  );

  const createFolder = useCallback(
    async (parentFolderId: string | null, name: string) => {
      if (!data || !snapshot) return false;
      try {
        if (localDemo) {
          const now = new Date().toISOString();
          const parent = parentFolderId
            ? snapshot.folders.find((folder) => folder.id === parentFolderId)
            : undefined;
          const id = crypto.randomUUID();
          setSnapshot({
            ...snapshot,
            folders: [
              ...snapshot.folders,
              {
                id,
                organization_id: data.organization.id,
                project_id: data.project.id,
                parent_folder_id: parentFolderId,
                name: name.trim(),
                created_by: snapshot.viewer_id,
                creator_name: data.viewer.full_name,
                created_at: now,
                updated_at: now,
                archived_at: null,
                archived_by: null,
                effective_archived: false,
                id_path: [...(parent?.id_path ?? []), id],
                name_path: [...(parent?.name_path ?? []), name.trim()],
              },
            ],
          });
        } else {
          await remoteRpc("save_project_folder", {
            p_folder_id: null,
            p_project_id: data.project.id,
            p_parent_folder_id: parentFolderId,
            p_name: name,
          });
          await finishRemoteMutation();
        }
        setError(null);
        return true;
      } catch (reason) {
        setError(messageFor(reason, "No se pudo crear la carpeta."));
        return false;
      }
    }, [data, finishRemoteMutation, localDemo, remoteRpc, snapshot]);

  const renameFolder = useCallback(
    async (folder: ProjectFolder, name: string) => {
      if (!data || !snapshot) return false;
      try {
        if (localDemo) {
          setSnapshot({
            ...snapshot,
            folders: snapshot.folders.map((candidate) =>
              candidate.id === folder.id
                ? { ...candidate, name: name.trim(), updated_at: new Date().toISOString() }
                : candidate,
            ),
          });
        } else {
          await remoteRpc("save_project_folder", {
            p_folder_id: folder.id,
            p_project_id: data.project.id,
            p_parent_folder_id: folder.parent_folder_id,
            p_name: name,
          });
          await finishRemoteMutation();
        }
        setError(null);
        return true;
      } catch (reason) {
        setError(messageFor(reason, "No se pudo renombrar la carpeta."));
        return false;
      }
    }, [data, finishRemoteMutation, localDemo, remoteRpc, snapshot]);

  const moveFolder = useCallback(
    async (folderId: string, parentFolderId: string | null) => {
      if (!snapshot) return false;
      try {
        if (localDemo) {
          setSnapshot({
            ...snapshot,
            folders: snapshot.folders.map((folder) =>
              folder.id === folderId
                ? { ...folder, parent_folder_id: parentFolderId, updated_at: new Date().toISOString() }
                : folder,
            ),
          });
        } else {
          await remoteRpc("move_project_folder", {
            p_folder_id: folderId,
            p_parent_folder_id: parentFolderId,
          });
          await finishRemoteMutation();
        }
        setError(null);
        return true;
      } catch (reason) {
        setError(messageFor(reason, "No se pudo mover la carpeta."));
        return false;
      }
    }, [finishRemoteMutation, localDemo, remoteRpc, snapshot]);

  const setFolderArchived = useCallback(
    async (folderId: string, archived: boolean) => {
      if (!snapshot) return false;
      try {
        if (localDemo) {
          setSnapshot({
            ...snapshot,
            folders: snapshot.folders.map((folder) =>
              folder.id === folderId
                ? {
                    ...folder,
                    archived_at: archived ? new Date().toISOString() : null,
                    archived_by: archived ? snapshot.viewer_id : null,
                    effective_archived: archived,
                  }
                : folder,
            ),
          });
        } else {
          await remoteRpc("set_project_folder_archived", {
            p_folder_id: folderId,
            p_archived: archived,
          });
          await finishRemoteMutation();
        }
        setError(null);
        return true;
      } catch (reason) {
        setError(messageFor(reason, "No se pudo actualizar la carpeta."));
        return false;
      }
    }, [finishRemoteMutation, localDemo, remoteRpc, snapshot]);

  const deleteFolder = useCallback(
    async (folderId: string) => {
      if (!snapshot) return false;
      try {
        if (localDemo) {
          setSnapshot({
            ...snapshot,
            folders: snapshot.folders.filter((folder) => folder.id !== folderId),
          });
        } else {
          await remoteRpc("delete_project_folder", { p_folder_id: folderId });
          await finishRemoteMutation();
        }
        setError(null);
        return true;
      } catch (reason) {
        setError(messageFor(reason, "No se pudo eliminar la carpeta."));
        return false;
      }
    }, [finishRemoteMutation, localDemo, remoteRpc, snapshot]);

  const renameFile = useCallback(
    async (fileId: string, name: string) => {
      if (!snapshot) return false;
      try {
        if (localDemo)
          setSnapshot({
            ...snapshot,
            files: snapshot.files.map((file) =>
              file.id === fileId
                ? { ...file, name: name.trim(), updated_at: new Date().toISOString() }
                : file,
            ),
          });
        else {
          await remoteRpc("rename_project_file", {
            p_file_id: fileId,
            p_name: name,
          });
          await finishRemoteMutation();
        }
        setError(null);
        return true;
      } catch (reason) {
        setError(messageFor(reason, "No se pudo renombrar el archivo."));
        return false;
      }
    }, [finishRemoteMutation, localDemo, remoteRpc, snapshot]);

  const moveFile = useCallback(
    async (fileId: string, folderId: string | null) => {
      if (!snapshot) return false;
      try {
        if (localDemo)
          setSnapshot({
            ...snapshot,
            files: snapshot.files.map((file) =>
              file.id === fileId
                ? { ...file, folder_id: folderId, updated_at: new Date().toISOString() }
                : file,
            ),
          });
        else {
          await remoteRpc("move_project_file", {
            p_file_id: fileId,
            p_folder_id: folderId,
          });
          await finishRemoteMutation();
        }
        setError(null);
        return true;
      } catch (reason) {
        setError(messageFor(reason, "No se pudo mover el archivo."));
        return false;
      }
    }, [finishRemoteMutation, localDemo, remoteRpc, snapshot]);

  const setFileArchived = useCallback(
    async (fileId: string, archived: boolean) => {
      if (!snapshot) return false;
      try {
        if (localDemo)
          setSnapshot({
            ...snapshot,
            files: snapshot.files.map((file) =>
              file.id === fileId
                ? {
                    ...file,
                    archived_at: archived ? new Date().toISOString() : null,
                    effective_archived: archived,
                  }
                : file,
            ),
          });
        else {
          await remoteRpc("set_project_file_archived", {
            p_file_id: fileId,
            p_archived: archived,
          });
          await finishRemoteMutation();
        }
        setError(null);
        return true;
      } catch (reason) {
        setError(messageFor(reason, "No se pudo actualizar el archivo."));
        return false;
      }
    }, [finishRemoteMutation, localDemo, remoteRpc, snapshot]);

  const deleteFile = useCallback(
    async (fileId: string) => {
      if (!snapshot) return false;
      try {
        if (localDemo) {
          setSnapshot({
            ...snapshot,
            files: snapshot.files.filter((file) => file.id !== fileId),
          });
        } else {
          const storagePath = await remoteRpc("request_project_file_delete", {
            p_file_id: fileId,
          });
          if (typeof storagePath !== "string")
            throw new Error("No se obtuvo la ruta segura del archivo.");
          if (!supabase) throw new Error("Supabase no está configurado.");
          const removed = await supabase.storage
            .from("project-files")
            .remove([storagePath]);
          if (removed.error) {
            await remoteRpc("cancel_project_file_delete", {
              p_file_id: fileId,
            });
            throw removed.error;
          }
          try {
            await remoteRpc("finalize_project_file_delete", {
              p_file_id: fileId,
            });
          } catch {
            throw new Error(
              "El objeto se eliminó y la metadata quedó pendiente de conciliación segura.",
            );
          }
          await finishRemoteMutation();
        }
        setError(null);
        return true;
      } catch (reason) {
        setError(messageFor(reason, "No se pudo eliminar el archivo."));
        return false;
      }
    }, [finishRemoteMutation, localDemo, remoteRpc, snapshot]);

  const upload = useCallback(
    async (
      file: File,
      folderId: string | null,
      category = "Otro",
      description = "",
    ) => {
      const succeeded = await uploadFile(
        file,
        category,
        description,
        null,
        folderId,
      );
      if (succeeded) await load();
      return succeeded;
    }, [load, uploadFile]);

  return {
    snapshot,
    loading,
    error,
    clearError: () => setError(null),
    reload: load,
    createFolder,
    renameFolder,
    moveFolder,
    setFolderArchived,
    deleteFolder,
    renameFile,
    moveFile,
    setFileArchived,
    deleteFile,
    upload,
    download: (file: ManagedFileRecord) =>
      downloadFile(file.storage_path, file.name),
  };
}
